import { __awaiter } from 'tslib';

/**
 * Check if the values is undefined
 *
 * code: ```value === undefined```
 * */
function isUndefined(value) {
    return value === undefined;
}
/**
 * Check if the values is null
 *
 * code: ```value === null```
 * */
function isNull(value) {
    return value === null;
}
/**
 * Check if the values is null or undefined
 *
 * code: ```value === undefined || value === null```
 * */
function isNullOrUndefined(value) {
    return isUndefined(value) || isNull(value);
}
/**
 * This function return `true` if the value is not null, not undefined and typeof is 'string', otherwise `false`
 *
 * code: ```!isNullOrUndefined(value) && typeof value === 'string'```
 *
 * @returns boolean
 * */
function isValidString(value) {
    return !isNullOrUndefined(value) && typeof value === 'string';
}
/**
 * This function return `true` if the value is not null, not undefined and typeof is 'function', otherwise `false`
 *
 * code: ```!isNullOrUndefined(value) && typeof value === 'function'```
 *
 * @returns boolean
 * */
function isValidFunction(value) {
    return !isNullOrUndefined(value) && typeof value === 'function';
}
/**
 * This function return `true` if the value is not null, not undefined, typeof is 'string' and different of '', otherwise `false`
 *
 * code: ```isValidString(value) && value !== ''```
 *
 * @returns boolean
 * */
function isNotEmptyString(value) {
    return isValidString(value) && value !== '';
}
/**
 * This function return `true` if the value is not null, not undefined, typeof is 'string' and different of '', otherwise `false`
 *
 * code: ```isValidString(value) && value !== ''```
 *
 * @returns boolean
 * */
function isDefinedString(value) { return isNotEmptyString(value); }
;
/**
 * This function return `true` if the value is not null, not undefined, typeof is 'string' and different of '', otherwise `false`
 *
 * code: ```isValidString(value) && value !== ''```
 *
 * @returns boolean
 * */
function isProvidedString(value) { return isNotEmptyString(value); }
;
/**
 * This function return `true` if the value is not null, not undefined, typeof is 'string' and different of '', otherwise `false`
 *
 * code: ```isValidString(value) && value !== ''```
 *
 * @returns boolean
 * */
function isFilledString(value) { return isNotEmptyString(value); }
;
/**
 * This function return `true` if the value is not null, not undefined, and equal to '', otherwise `false`
 *
 * code: ```isValidString(value) && value === ''```
 *
 * @returns boolean
 */
function isEmptyString(value) {
    return isValidString(value) && value === '';
}
/**
 * This function return `true` if value's type is 'number', otherwise `false
 *
 * code: ```typeof value === 'number'```
 *
 * @returns boolean
 * */
function isNumber(value) {
    return typeof value === 'number';
}
/**
 * This function return `true` if value is a number and is not a NaN
 *
 * code: ```return isNumber(value) && !isNaN(value)```
 *
 * @returns boolean
 * */
function isNotNaNNumber(value) {
    return isNumber(value) && !isNaN(value);
}
/**
 * @returns boolean
 *
 * return `true` if typeof is 'number' and isNaN, otherwise `false`
 *
 * ```return isNumber(value) && isNaN(value)```
 * */
function isNaNNumber(value) {
    return isNumber(value) && isNaN(value);
}
/**
 * @returns boolean
 *
 * return `true` if the value is not null, not undefined, typeof if 'number' and it's not a NaN number, otherwise `false`
 *
 * ```!isNullOrUndefined(value) && isNotNaNNumber(value)```
 * */
function isValidNumber(value) {
    return !isNullOrUndefined(value) && isNotNaNNumber(value);
}
/**
 * ```!isNaN(parseFloat(value as string))```
 * */
function isFloat(value) {
    return !isNaN(parseFloat(value));
}
/**
 * ```isFloat(value) && isFloatGreaterThan(value)```
 * */
function isPositiveFloat(value) {
    return isFloat(value) && isFloatGreaterThan(value);
}
/**
 * ```isFloat(value) && isFloat(valueToCompare) && parseFloat(value) > valueToCompare```
 * */
function isFloatGreaterThan(value, valueToCompare = 0) {
    return isFloat(value) && isFloat(valueToCompare) && parseFloat(value) > valueToCompare;
}
/**
 * ```isFloat(value) && isFloat(valueToCompare) && parseFloat(value) < valueToCompare```
 * */
function isFloatLessThan(value, valueToCompare = 0) {
    return isFloat(value) && isFloat(valueToCompare) && parseFloat(value) < valueToCompare;
}
/**
 * ```typeof value === 'string'```
 * */
function isString(value) {
    return typeof value === 'string';
}
/**
 * ```!isNullOrUndefined(value) && Array.isArray(value)```
 * */
function isArray(value) {
    return !isNullOrUndefined(value) && Array.isArray(value);
}
/**
 * This function return `true` if the value is not null, not undefined, is an array and length is greater than zero, otherwise `false`
 *
 * code: ```isArray(value) && value.length > 0```
 *
 * @returns boolean
 * */
function isNoEmptyArray(value) {
    return isArray(value) && value.length > 0;
}
/**
 * This function return `true` if the value is not null, not undefined, is an array and length is greater than zero, otherwise `false`
 *
 * code: ```isArray(value) && value.length > 0```
 *
 * @returns boolean
 * */
function isFilledArray(value) { return isNoEmptyArray(value); }
;
/**
 * ```!isNullOrUndefined(value) && typeof value === 'object'```
 * */
function isObject(value) {
    return !isNullOrUndefined(value) && typeof value === 'object';
}
/**
 * Default: milliseconds = 1000
 *
 * ```new Promise<void>((resolve) => setTimeout(() => resolve(), milliseconds))```
 * */
function delay(milliseconds = 1000) {
    return new Promise((resolve) => setTimeout(() => resolve(), milliseconds));
}
/**
 * ```!isNullOrUndefined(value) && typeof value === 'object'```
 * */
function closetValue(array, goal) {
    return !isNoEmptyArray(array) ? null : array.reduce((prev, curr) => Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev);
}
/**
 * ```(isString(value) && isString(toRemove)) ? value.split(toRemove).join('') : value```
 * */
function removeAllSubStr(value, toRemove) {
    return (isString(value) && isString(toRemove)) ? value.split(toRemove).join('') : value;
}
/**
 * ```!isNullOrUndefined(new URL(value))```
 * */
function isValidHttpUrl(value) {
    try {
        return !isNullOrUndefined(new URL(value));
    }
    catch (error) {
        return false;
    }
    ;
}

/**
 * ```str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)```
 * */
function camelToSnakeCase(str) {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}
/**
 * ```str.replace(/(\_\w)/g, (m) => m[1].toUpperCase())```
 * */
function snakeToCamelCase(str) {
    return str.replace(/(\_\w)/g, (m) => m[1].toUpperCase());
}
/**
 * To camelCase
 * */
function sentenceToCamelCase(str) {
    return str.replace(/\s+(.)/g, (match, group) => group.toUpperCase()).trim();
}
/**
 * Replace withe spaces with the second parameter, by default will just remove it.
 *
 * Input: `replaceSpaces('AAA ee BB H h j')`
 * Output: 'AAAeeBBHhj'
 *
 * Input: `replaceSpaces('AAA ee BB H h j', '_')`
 * Output: 'AAA_ee_BB_H_h_j'
 * */
function replaceSpaces(str, newValue = '') {
    return replaceCharacters(str, ' ', newValue);
}
/**
 * Replace any character, by default will just find space characters and remove it.
 *
 * Input: `replaceSpaces('AAA ee BB H h j')`
 * Output: 'AAAeeBBHhj'
 *
 * Input: `replaceSpaces('AAA ee BB H h j', ' ', '+')`
 * Output: 'AAA+ee+BB+H+h+j'
 * */
function replaceCharacters(str, character = ' ', newValue = '') {
    return str.replace(new RegExp(character, 'g'), newValue).trim();
}
/**
 * Input: 'AAA ee BB H h j'
 * Output: 'AAAEe_BB_HHJ'
 * */
function sentenceToConstName(str) {
    return str.replace(/\s+[A-Z]/g, letter => `_${letter.toUpperCase()}`).replace(/\s+(.)/g, (match, group) => group.toUpperCase()).trim();
}
/**
 * Input: 'AAA ee BB H h j'
 * Output: 'AAAEe_BB_HHJ'
 * */
function sentenceToSnakeCase(str) {
    return camelToSnakeCase(sentenceToCamelCase(str).split('').map((string, i) => i === 0 ? string.toLowerCase() : string).join(''));
}
function getFileNameExtension(str) {
    return (isString(str) && !str.includes('.')) ? null : str.split('').reverse().join('').split('.')[0].split('').reverse().join('');
}
function toSentenceCase(str) {
    return str.replace(/(\_\w)/g, (m) => ' ' + m[1]).replace(/[A-Z]/g, l => ` ${l}`).replace(/\s+/g, ' ').toLocaleLowerCase().trim().replace(/^\w/, l => l.toUpperCase());
}
/**
 * Check if the string is a email
 * */
function emailIsValid(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
/**
 * Check if the string is alphabetic.
 *
 * You can allow any other character as second parameter, by default blank space is allowed.
 *
 * By default don't allow empty
 * */
function isAlpha(str, allow = ' ', allowEmpty = false) {
    return isValidString(str) && new RegExp(`^${allowEmpty ? '$|' : ''}[a-z${allow}]+$`, 'i').test(str);
}
/**
 * Check if the string is alphabetic without blank spaces.
 *
 * By default don't allow empty
 * */
function isAlphaWithoutSpaces(str, allowEmpty = false) {
    return isAlpha(str, '', allowEmpty);
}
/**
 * Check if the string is alphanumeric.
 *
 * You can allow any other character as second parameter.
 *
 * By default don't allow empty
 * */
function isAlphanumeric(str, allow = '', allowEmpty = false) {
    return isValidString(str) && new RegExp(`^${allowEmpty ? '$|' : ''}[a-z0-9${allow}]+$`, 'i').test(str);
}
/**
 * Check if the string is numeric.
 *
 * You can allow any other character as second parameter.
 *
 * By default don't allow empty
 * */
function isNumeric(str, allow = '', allowEmpty = false) {
    return isValidString(str) && new RegExp(`^${allowEmpty ? '$|' : ''}[0-9${allow}]+$`, 'i').test(str);
}
/**
 * Check if the string is alphanumeric and can have spaces and dash.
 *
 * By default don't allow empty
 * */
function isAlphanumericSpaceAndDash(str, allowEmpty = false) {
    return isAlphanumeric(str, ' /', allowEmpty);
}
/**
 * Check if the string is alphanumeric and can have dot, spaces and dash.
 * */
function isAlphanumericDotSpaceAndDash(str) {
    return isAlphanumeric(str, '. /');
}
/**
 * Check if the string is alphanumeric and can have dot, spaces and dash or can be a empty string.
 * */
function IsEmptyOrAlphanumericDotSpaceAndDash(str) {
    return isAlphanumeric(str, '. /', true);
}
/**
 * Check if the string has a number.
 * */
function hasANumber(str) {
    return isValidString(str) && new RegExp(`\\d`).test(str);
}
/**
 * Check if the string has a lowercase letter.
 * */
function hasALowercaseLetter(str) {
    return isValidString(str) && new RegExp(`(?=.*[a-z])`).test(str);
}
/**
 * Check if the string has a uppercase letter.
 * */
function hasAUppercaseLetter(str) {
    return isValidString(str) && new RegExp(`(?=.*[A-Z])`).test(str);
}
/**
 * Check if the string has a special character like: ```-+_!@#$%^&*., ?```
 * */
function hasASpecialCharacter(str) {
    return isValidString(str) && new RegExp(`(?=.*[-+_!@#$%^&*., ?])`).test(str);
}

function urlFromBlob(blob) {
    return window.URL.createObjectURL(blob);
}
function blobFromDataArray(dataArray) {
    return new Blob([new Uint8Array(dataArray).buffer]);
}
function blobFromBuffer(buffer) {
    return new Blob([buffer]);
}
function blobFromString(stringBase64) {
    return fetch(stringBase64).then(image => image.blob());
}
function blobFromUrl(url, headers = {}) {
    return fetch(url, { headers }).then(image => image.blob());
}
function convertBlobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            resolve(reader.result);
        };
        reader.readAsDataURL(blob);
    });
}
function downloadBlobFile(blob, fileName) {
    const url = urlFromBlob(blob);
    // IE 11
    if (window.navigator.msSaveBlob !== undefined) {
        window.navigator.msSaveBlob(blob, fileName);
        return;
    }
    downloadFileFromUrl(url, fileName);
}
function downloadFileFromUrl(url, fileName) {
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    requestAnimationFrame(() => {
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    });
}
function downloadByFileUrl(url, fileName = 'file') {
    return __awaiter(this, void 0, void 0, function* () {
        const blob = yield blobFromUrl(url);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.href = urlFromBlob(blob);
        a.download = fileName;
        a.click();
        document.body.removeChild(a);
    });
}
function downloadByFileUrlAsHref(url, fileName = 'file') {
    return __awaiter(this, void 0, void 0, function* () {
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.href = url;
        a.download = fileName;
        a.click();
        document.body.removeChild(a);
    });
}
function textToBlob(text) {
    return new Blob([text], { type: 'text/plain' });
}

/**
 * isValidNumber( value ) && value % 2 === 0
 * */
function isEven(value) {
    return isValidNumber(value) && value % 2 === 0;
}
/**
 * isValidNumber(value) && !isEven(value)
 * */
function isOdd(value) {
    return isValidNumber(value) && !isEven(value);
}

/*
 * Public API Surface of util
 */

/**
 * Generated bundle index. Do not edit.
 */

export { IsEmptyOrAlphanumericDotSpaceAndDash, blobFromBuffer, blobFromDataArray, blobFromString, blobFromUrl, camelToSnakeCase, closetValue, convertBlobToBase64, delay, downloadBlobFile, downloadByFileUrl, downloadByFileUrlAsHref, emailIsValid, getFileNameExtension, hasALowercaseLetter, hasANumber, hasASpecialCharacter, hasAUppercaseLetter, isAlpha, isAlphaWithoutSpaces, isAlphanumeric, isAlphanumericDotSpaceAndDash, isAlphanumericSpaceAndDash, isArray, isDefinedString, isEmptyString, isEven, isFilledArray, isFilledString, isFloat, isFloatGreaterThan, isFloatLessThan, isNaNNumber, isNoEmptyArray, isNotEmptyString, isNotNaNNumber, isNull, isNullOrUndefined, isNumber, isNumeric, isObject, isOdd, isPositiveFloat, isProvidedString, isString, isUndefined, isValidFunction, isValidHttpUrl, isValidNumber, isValidString, removeAllSubStr, replaceCharacters, replaceSpaces, sentenceToCamelCase, sentenceToConstName, sentenceToSnakeCase, snakeToCamelCase, textToBlob, toSentenceCase, urlFromBlob };
